import { Link } from '@material-ui/core';
import { RouteWidgetResult } from '@services/requests/routeCalculator/routesCalculationService/types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RouteWidgetParams } from '../../../types';
import useDelivery from '../../../useDelivery';
import { Alert } from './Alert';

interface RoutesSerchResultAlertProps {
  routes: RouteWidgetResult;
  routeParams: RouteWidgetParams;
}

function RoutesSerchResultAlert({ routes, routeParams }: RoutesSerchResultAlertProps) {
  const { useIsLoading, useIsRoutesLoading, useDeliverySearchParams } = useDelivery();
  const [deliverySearchParams] = useDeliverySearchParams().useState();
  const [fixedRouteParams] = useState<RouteWidgetParams>(routeParams);
  const [isLoading] = useIsLoading().useState();
  const [isRoutesLoading] = useIsRoutesLoading().useState();

  const { t } = useTranslation(['RoutesListComponent', 'Common']);

  const alternativeParams = [
    fixedRouteParams.isNeedPrekeridge,
    !fixedRouteParams.isNeedContainerRent,
    fixedRouteParams.isWithoutPrekeridge,
    fixedRouteParams.isWithoutOnkeridge,
    fixedRouteParams.isFromTerminalRadius,
    fixedRouteParams.isToTerminalRadius,
    fixedRouteParams.isWithCrossDocking,
  ];

  const isAllAlternativeVariantsDisabled = alternativeParams.every((flag) => flag === false);
  const isSomeAlternativeVariantsEnabled = alternativeParams.some((flag) => flag === true);

  const detailsTextCommon = (
    <>
      <span>{t('RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Title')}</span>
      <ul>
        <li>
          <b>
            {t(
              'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.withPreCarriage.Name'
            )}
          </b>{' '}
          {t(
            'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.withPreCarriage.Text'
          )}
        </li>
        <li>
          <b>
            {t(
              'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.withoutPreCarriage.Name'
            )}
          </b>{' '}
          {t(
            'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.withoutPreCarriage.Text'
          )}
        </li>
        <li>
          <b>
            {t(
              'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.withoutOnCarriage.Name'
            )}
          </b>{' '}
          {t(
            'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.withoutOnCarriage.Text'
          )}
        </li>
        <li>
          <b>
            {t(
              'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.fromTerminalRadius.Name'
            )}
          </b>{' '}
          {t(
            'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.fromTerminalRadius.Text'
          )}
        </li>
        <li>
          <b>
            {t(
              'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.toTerminalRadius.Name'
            )}
          </b>{' '}
          {t(
            'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.toTerminalRadius.Text'
          )}
        </li>
        <li>
          <b>{t('RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.soc.Name')}</b>{' '}
          {t('RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.soc.Text')}
        </li>
        <li>
          <b>
            {t(
              'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.withCrossDocking.Name'
            )}
          </b>{' '}
          {t(
            'RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Items.withCrossDocking.Text'
          )}
        </li>
      </ul>
      <span>{t('RoutesListComponent:AlternativeVariants.AlertDetailsCommon.Footer')}</span>
    </>
  );

  if (isLoading || isRoutesLoading || Object.keys(routes.cargo).length === 0) {
    return null;
  }

  if (!routes.isNotCompleteResult && isAllAlternativeVariantsDisabled && routes.total !== 0) {
    const text = (
      <>
        <span className="count-title">
          {t('RoutesListComponent:routesList.CompleteResult.Count')} <b>{routes.total}</b>
        </span>
        <p className="text">{t('RoutesListComponent:routesList.CompleteResult.Text1')}</p>
      </>
    );

    return <Alert color="success" text={text} detailsText={detailsTextCommon} />;
  }

  if (routes.isNotCompleteResult && isAllAlternativeVariantsDisabled && routes.total !== 0) {
    const text = (
      <>
        <span className="count-title">
          {t('RoutesListComponent:routesList.NotCompleteResult.Count')} <b>{routes.total}</b>
        </span>
        <p className="text">{t('RoutesListComponent:routesList.NotCompleteResult.Text1')}</p>
        <p className="text">{t('RoutesListComponent:routesList.NotCompleteResult.Text2')}</p>
      </>
    );

    return (
      <Alert
        color="warning"
        title={t('RoutesListComponent:routesList.NotCompleteResult.Title')}
        text={text}
        detailsText={detailsTextCommon}
      />
    );
  }

  if (isAllAlternativeVariantsDisabled && routes.total === 0) {
    const text = (
      <>
        <span className="count-title">
          {t('RoutesListComponent:routesList.NotFound.RadiusPart1')}{' '}
          <b>{deliverySearchParams?.calculation_max_radius_km}</b>{' '}
          {t('RoutesListComponent:routesList.NotFound.RadiusPart2')}
        </span>
        <p className="text">
          {t('RoutesListComponent:routesList.NotFound.Text')}{' '}
          <Link href={t('Common:ContactsLink')} underline="hover" style={{ fontSize: '14px' }}>
            {t('RoutesListComponent:routesList.NotFound.Form')}
          </Link>
        </p>
      </>
    );

    return <Alert color="error" text={text} detailsText={detailsTextCommon} />;
  }

  if (isSomeAlternativeVariantsEnabled && routes.total !== 0) {
    const text = (
      <>
        <span className="count-title">
          {t('RoutesListComponent:routesList.SomeAlternativeParamEnabledFound.Count')}{' '}
          <b>{routes?.total}</b>
        </span>
        <p className="text">
          {t('RoutesListComponent:routesList.SomeAlternativeParamEnabledFound.Filter')}
        </p>
      </>
    );

    const detailsText = (
      <>
        <ul>
          <li>
            <b>{t('RoutesListComponent:AlternativeVariants.Variants.exactSearch')}</b>:{' '}
            {routes.countExactSearch}
          </li>
          <li>
            <b>{t('RoutesListComponent:AlternativeVariants.Variants.fromTerminalRadius')}</b>:{' '}
            {routes.countFromTerminalRadius}
          </li>
          <li>
            <b>{t('RoutesListComponent:AlternativeVariants.Variants.toTerminalRadius')}</b>:{' '}
            {routes.countToTerminalRadius}
          </li>
          <li>
            <b>{t('RoutesListComponent:AlternativeVariants.Variants.withPreCarriage')}</b>:{' '}
            {routes.countWithPreCarriage}
          </li>
          <li>
            <b>{t('RoutesListComponent:AlternativeVariants.Variants.withoutOnCarriage')}</b>:{' '}
            {routes.countWithoutOnCarriage}
          </li>
          <li>
            <b>{t('RoutesListComponent:AlternativeVariants.Variants.withoutPreCarriage')}</b>:{' '}
            {routes.countWithoutPreCarriage}
          </li>
          <li>
            <b>{t('RoutesListComponent:AlternativeVariants.Variants.withCrossDocking')}</b>:{' '}
            {routes.countWithCrossDocking}
          </li>
        </ul>
        <br />
        {detailsTextCommon}
      </>
    );

    return <Alert color="success" text={text} detailsText={detailsText} />;
  }

  if (isSomeAlternativeVariantsEnabled && routes.total === 0) {
    const text = (
      <>
        <span className="count-title">
          {t('RoutesListComponent:routesList.SomeAlternativeParamEnabledNotFound.NotFound')}
        </span>
        <p className="text">
          {t('RoutesListComponent:routesList.SomeAlternativeParamEnabledNotFound.Text')}{' '}
          <Link href={t('Common:ContactsLink')} underline="hover" style={{ fontSize: '14px' }}>
            {t('RoutesListComponent:routesList.SomeAlternativeParamEnabledNotFound.Form')}
          </Link>
        </p>
      </>
    );

    return <Alert color="error" text={text} detailsText={detailsTextCommon} />;
  }

  return null;
}

export { RoutesSerchResultAlert };
